import { ADDON_CATALOG, BillingType, ClientAddon, ClientBilling } from "../../types";
import { formatCommissionRate } from "../../utils";
import type { SetRatePayload } from "../../hooks";

// =============================================================================
// ADD-ON EDIT-STATE HELPERS
// =============================================================================

/** Editable form state for one add-on. Prices/dates are strings while editing. */
export type AddonDraft = {
  type: string;
  displayName: string;
  enabled: boolean;
  monthlyPrice: string;
  startDate: string;
  trialEnabled: boolean;
};

/** Merge the client's configured add-ons with the catalog so every known add-on
 *  type gets an editable row, even one the client hasn't configured yet. */
export function buildAddonDrafts(addons: ClientAddon[] | undefined): AddonDraft[] {
  const configuredByType = new Map((addons ?? []).map((a) => [a.type, a]));
  return ADDON_CATALOG.map((catalog) => {
    const configured = configuredByType.get(catalog.type);
    return {
      type: catalog.type,
      displayName: configured?.displayName ?? catalog.displayName,
      enabled: configured?.enabled ?? false,
      monthlyPrice:
        configured?.monthlyPrice != null
          ? configured.monthlyPrice.toString()
          : "",
      startDate: configured?.startDate ?? "",
      trialEnabled: configured?.trialEnabled ?? false,
    };
  });
}

/** A draft differs from what's persisted when any of its fields changed. */
export function addonDraftChanged(
  draft: AddonDraft,
  addons: ClientAddon[] | undefined,
): boolean {
  const configured = (addons ?? []).find((a) => a.type === draft.type);
  const persistedPrice =
    configured?.monthlyPrice != null ? configured.monthlyPrice.toString() : "";
  return (
    draft.enabled !== (configured?.enabled ?? false) ||
    draft.monthlyPrice.trim() !== persistedPrice ||
    draft.startDate !== (configured?.startDate ?? "") ||
    draft.trialEnabled !== (configured?.trialEnabled ?? false)
  );
}

// =============================================================================
// BILLING CONFIG DRAFT
// =============================================================================

export type BillingConfigDraft = {
  rate: string;
  markup: string;
  onboardingFee: boolean;
  repMgmtWaiver: boolean;
  billingType: BillingType;
  platformFeeAmount: string;
  platformFeeDescription: string;
  minimumFeeAmount: string;
  addons: AddonDraft[];
};

export function draftFrom(client: ClientBilling): BillingConfigDraft {
  return {
    rate: formatCommissionRate(client.commissionRate),
    markup: client.displayCommissionMarkup != null ? (client.displayCommissionMarkup * 100).toString() : "4",
    onboardingFee: client.showOnboardingFee ?? false,
    repMgmtWaiver: client.showReputationManagementWaiver ?? true,
    billingType: client.billingType || "commission",
    platformFeeAmount: client.platformFeeAmount?.toString() || "",
    platformFeeDescription: client.platformFeeDescription || "",
    minimumFeeAmount: client.minimumFeeAmount?.toString() || "",
    addons: buildAddonDrafts(client.addons),
  };
}

const inRange = (s: string, min: number, max: number) => {
  const n = parseFloat(s.trim());
  return s.trim() !== "" && !isNaN(n) && n > min && n <= max;
};

/** Mirrors the backend rules so the admin doesn't discover them as a failed save. */
export function validateDraft(d: BillingConfigDraft, changedAddons: AddonDraft[]): string | null {
  const rate = parseFloat(d.rate);
  if (isNaN(rate) || rate < 0 || rate > 100) return "Rate must be between 0 and 100";
  if (d.billingType === "commission_or_flat" && !inRange(d.minimumFeeAmount, 0, 50000))
    return "Enter a minimum monthly fee between $0.01 and $50,000 to use Commission or Flat Fee billing";
  for (const a of changedAddons) {
    if (a.enabled && !inRange(a.monthlyPrice, 0, 50000))
      return `Enter a monthly price between $0.01 and $50,000 to enable ${a.displayName}`;
  }
  return null;
}

/** Assumes validateDraft returned null — the rate is parsed unguarded. */
export function ratePayloadFrom(
  d: BillingConfigDraft,
  client: ClientBilling,
): SetRatePayload {
  return {
    userId: client.userId,
    commissionRate: parseFloat(d.rate) / 100,
    billingEmail: client.stripeEmail || client.email || "",
    billingType: d.billingType,
    showOnboardingFee: d.onboardingFee,
    showReputationManagementWaiver: d.repMgmtWaiver,
    _silentToast: true as const,
    ...(d.markup ? { displayCommissionMarkup: parseFloat(d.markup) / 100 } : {}),
    ...(d.platformFeeAmount ? { platformFeeAmount: parseFloat(d.platformFeeAmount) } : {}),
    ...(d.platformFeeDescription ? { platformFeeDescription: d.platformFeeDescription } : {}),
    ...(d.minimumFeeAmount ? { minimumFeeAmount: parseFloat(d.minimumFeeAmount) } : {}),
  };
}
